import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { 
  Award, Play, Clock, CheckCircle, XCircle, Calendar, 
  FileText, Download, Upload, Lock, Unlock, AlertCircle,
  Eye, Send, RefreshCcw, HelpCircle, Timer, FileUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Resolve relative upload paths to an absolute backend URL (avoid /api/api). */
const resolveUploadUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  const withApi = path.startsWith('/api/') ? path : `/api${path}`;
  return `${API_URL}${withApi}`;
};

const EvaluationsPage = ({ onMenuClick }) => {
  const { lastMessage } = useWebSocket();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Submit dialog
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitAnswers, setSubmitAnswers] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Result dialog
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  const fetchEvaluations = useCallback(async () => {
    try {
      const response = await api.get('/evaluations');
      setEvaluations(response.data);
    } catch (error) {
      console.error('Error fetching evaluations:', error);
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvaluations();
  }, [fetchEvaluations]);

  // WebSocket refresh
  useEffect(() => {
    if (lastMessage?.type === 'data_refresh' && lastMessage?.event_type?.includes('evaluation')) {
      fetchEvaluations();
    }
  }, [lastMessage, fetchEvaluations]);

  const handleOpenSubmit = async (evaluation) => {
    // Fetch full details
    try {
      const response = await api.get(`/evaluations/${evaluation.id}`);
      setSelectedEvaluation(response.data);
      setShowSubmitDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'évaluation');
    }
  };

  const handleSubmit = async () => {
    if (!submitFile && !submitAnswers) {
      toast.error('Veuillez joindre un fichier ou entrer des réponses');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (submitFile) {
        formData.append('file', submitFile);
      }
      if (submitAnswers) {
        formData.append('answers', JSON.stringify({ text: submitAnswers }));
      }

      await api.post(`/evaluations/${selectedEvaluation.id}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Soumission enregistrée avec succès');
      setShowSubmitDialog(false);
      setSubmitFile(null);
      setSubmitAnswers('');
      fetchEvaluations();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewResult = (evaluation) => {
    setSelectedResult(evaluation);
    setShowResultDialog(true);
  };

  const getStatusInfo = (evaluation) => {
    const statuses = {
      upcoming: { 
        icon: Lock, 
        label: 'À venir', 
        color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        description: 'Cette évaluation ouvrira le ' + (evaluation.scheduled_date 
          ? new Date(evaluation.scheduled_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          : 'bientôt')
      },
      open: { 
        icon: Unlock, 
        label: 'En cours', 
        color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
        description: 'Vous pouvez soumettre votre travail maintenant'
      },
      closed: { 
        icon: XCircle, 
        label: 'Terminée', 
        color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
        description: 'Cette évaluation est fermée'
      },
      available: { 
        icon: Play, 
        label: 'Disponible', 
        color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
        description: 'Vous pouvez soumettre votre travail'
      }
    };
    return statuses[evaluation.status] || statuses.available;
  };

  const getSubmissionStatus = (evaluation) => {
    if (!evaluation.my_submission) return null;
    
    const sub = evaluation.my_submission;
    if (sub.status === 'graded') {
      const passed = sub.score >= evaluation.passing_score;
      return {
        icon: passed ? CheckCircle : AlertCircle,
        label: passed ? 'Réussi' : 'À améliorer',
        color: passed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700',
        score: sub.score
      };
    }
    return {
      icon: Clock,
      label: 'En attente de correction',
      color: 'bg-blue-100 text-blue-700'
    };
  };

  // Stats
  const stats = {
    total: evaluations.length,
    upcoming: evaluations.filter(e => e.status === 'upcoming').length,
    toComplete: evaluations.filter(e => e.can_submit && !e.my_submission).length,
    completed: evaluations.filter(e => e.my_submission).length
  };

  const filteredEvaluations = evaluations.filter(evaluation => {
    if (activeTab === 'all') return true;
    if (activeTab === 'upcoming') return evaluation.status === 'upcoming';
    if (activeTab === 'available') return evaluation.can_submit && !evaluation.my_submission;
    if (activeTab === 'submitted') return evaluation.my_submission;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mes Évaluations" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Award className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">À venir</p>
                  <p className="text-2xl font-bold">{stats.upcoming}</p>
                </div>
                <Lock className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">À faire</p>
                  <p className="text-2xl font-bold">{stats.toComplete}</p>
                </div>
                <Play className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-80">Soumis</p>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex justify-between items-center mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white dark:bg-slate-800 border">
              <TabsTrigger value="all" data-testid="tab-all">Toutes</TabsTrigger>
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">À venir</TabsTrigger>
              <TabsTrigger value="available" data-testid="tab-available">À faire</TabsTrigger>
              <TabsTrigger value="submitted" data-testid="tab-submitted">Soumises</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Button variant="outline" size="icon" onClick={fetchEvaluations}>
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>

        {/* Evaluations Grid */}
        {filteredEvaluations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEvaluations.map((evaluation) => {
              const statusInfo = getStatusInfo(evaluation);
              const submissionStatus = getSubmissionStatus(evaluation);
              const StatusIcon = statusInfo.icon;
              
              return (
                <Card 
                  key={evaluation.id} 
                  className="hover:shadow-xl transition-all"
                  data-testid={`evaluation-card-${evaluation.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        evaluation.status === 'upcoming' ? 'bg-blue-100 dark:bg-blue-900/30' :
                        evaluation.status === 'open' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                        'bg-violet-100 dark:bg-violet-900/30'
                      }`}>
                        <StatusIcon className={`w-6 h-6 ${
                          evaluation.status === 'upcoming' ? 'text-blue-600' :
                          evaluation.status === 'open' ? 'text-emerald-600' :
                          'text-violet-600'
                        }`} />
                      </div>
                      <Badge className={statusInfo.color}>
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg mb-1">{evaluation.title}</CardTitle>
                    {evaluation.course_name && (
                      <p className="text-xs text-slate-500">{evaluation.course_name}</p>
                    )}
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                      {evaluation.description || 'Aucune description'}
                    </p>
                    
                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Note max</p>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {evaluation.max_score} pts
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Note passage</p>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {evaluation.passing_score} pts
                        </p>
                      </div>
                    </div>
                    
                    {/* Dates */}
                    <div className="space-y-2 mb-4">
                      {evaluation.scheduled_date && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span>
                            Ouverture: {new Date(evaluation.scheduled_date).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      {evaluation.end_date && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Timer className="w-4 h-4 text-red-600" />
                          <span>
                            Fermeture: {new Date(evaluation.end_date).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      {evaluation.duration_minutes && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Clock className="w-4 h-4 text-violet-600" />
                          <span>Durée: {evaluation.duration_minutes} minutes</span>
                        </div>
                      )}
                      {evaluation.exam_document_url && (
                        <div className="flex items-center gap-2 text-sm text-emerald-600">
                          <FileText className="w-4 h-4" />
                          <span>Document d'examen disponible</span>
                        </div>
                      )}
                    </div>

                    {/* Submission Status */}
                    {submissionStatus && (
                      <div className={`p-3 rounded-lg mb-4 ${submissionStatus.color}`}>
                        <div className="flex items-center gap-2">
                          <submissionStatus.icon className="w-4 h-4" />
                          <span className="font-medium">{submissionStatus.label}</span>
                          {submissionStatus.score !== undefined && (
                            <span className="ml-auto font-bold">
                              {submissionStatus.score} / {evaluation.max_score}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {evaluation.my_submission ? (
                      <Button 
                        className="w-full"
                        variant="outline"
                        onClick={() => handleViewResult(evaluation)}
                        data-testid={`view-result-${evaluation.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Voir ma soumission
                      </Button>
                    ) : evaluation.can_submit ? (
                      <Button 
                        className="w-full bg-violet-600 hover:bg-violet-700"
                        onClick={() => handleOpenSubmit(evaluation)}
                        data-testid={`submit-evaluation-${evaluation.id}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Soumettre ma réponse
                      </Button>
                    ) : (
                      <Button 
                        className="w-full"
                        disabled
                        variant="outline"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        {evaluation.status === 'upcoming' 
                          ? `Ouverture le ${new Date(evaluation.scheduled_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`
                          : 'Non disponible'
                        }
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600 dark:text-slate-400">
                {activeTab === 'all' 
                  ? 'Aucune évaluation disponible pour le moment'
                  : 'Aucune évaluation dans cette catégorie'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-600" />
              {selectedEvaluation?.title}
            </DialogTitle>
            <DialogDescription>
              Soumettez votre travail avant la date de fermeture
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvaluation && (
            <div className="space-y-6 py-4">
              {/* Info */}
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  {selectedEvaluation.description || 'Aucune instruction spécifique'}
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Award className="w-4 h-4 text-violet-600" />
                    Note max: {selectedEvaluation.max_score} pts
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    Note de passage: {selectedEvaluation.passing_score} pts
                  </span>
                </div>
              </div>

              {/* Exam Document Download */}
              {selectedEvaluation.exam_document_url && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800 dark:text-emerald-300">
                          Document d'examen
                        </p>
                        <p className="text-sm text-emerald-600 dark:text-emerald-400">
                          {selectedEvaluation.exam_document_name || 'Téléchargez le sujet'}
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => {
                        const fullUrl = resolveUploadUrl(selectedEvaluation.exam_document_url);
                        if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
                      }}
                      data-testid="download-exam-doc"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
              )}

              {/* File Upload */}
              {selectedEvaluation.allow_file_submission && (
                <div>
                  <Label>Votre fichier de réponse</Label>
                  <div className="mt-2 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.odt,.rtf,.txt,.zip"
                      onChange={(e) => setSubmitFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="submit-file-upload"
                      data-testid="submit-file-input"
                    />
                    <label htmlFor="submit-file-upload" className="cursor-pointer">
                      {submitFile ? (
                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                          <FileUp className="w-6 h-6" />
                          <span className="font-medium">{submitFile.name}</span>
                        </div>
                      ) : (
                        <div className="text-slate-500">
                          <Upload className="w-10 h-10 mx-auto mb-2" />
                          <p className="font-medium">Cliquez pour ajouter votre fichier</p>
                          <p className="text-xs mt-1">PDF, Word, ODT, TXT, ZIP (max 50MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              )}

              {/* Text Answer */}
              <div>
                <Label>Réponse écrite (optionnel)</Label>
                <Textarea
                  value={submitAnswers}
                  onChange={(e) => setSubmitAnswers(e.target.value)}
                  placeholder="Vous pouvez également ajouter des commentaires ou une réponse écrite..."
                  rows={4}
                  className="mt-2"
                  data-testid="submit-text-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting || (!submitFile && !submitAnswers)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="confirm-submit"
            >
              {submitting ? 'Envoi en cours...' : 'Soumettre ma réponse'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-violet-600" />
              {selectedResult?.title}
            </DialogTitle>
            <DialogDescription>
              Détails de votre soumission
            </DialogDescription>
          </DialogHeader>
          
          {selectedResult?.my_submission && (
            <div className="space-y-4 py-4">
              {/* Score */}
              {selectedResult.my_submission.score !== null ? (
                <div className={`p-6 rounded-lg text-center ${
                  selectedResult.my_submission.score >= selectedResult.passing_score
                    ? 'bg-emerald-50 dark:bg-emerald-900/20'
                    : 'bg-amber-50 dark:bg-amber-900/20'
                }`}>
                  <div className="text-4xl font-bold mb-2">
                    {selectedResult.my_submission.score} / {selectedResult.max_score}
                  </div>
                  <Badge className={
                    selectedResult.my_submission.score >= selectedResult.passing_score
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }>
                    {selectedResult.my_submission.score >= selectedResult.passing_score
                      ? 'Réussi'
                      : 'À améliorer'
                    }
                  </Badge>
                </div>
              ) : (
                <div className="p-6 rounded-lg text-center bg-blue-50 dark:bg-blue-900/20">
                  <Clock className="w-10 h-10 mx-auto mb-2 text-blue-600" />
                  <p className="font-medium text-blue-700 dark:text-blue-400">
                    En attente de correction
                  </p>
                </div>
              )}

              {/* Submission Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Date de soumission</span>
                  <span className="font-medium">
                    {new Date(selectedResult.my_submission.submitted_at).toLocaleDateString('fr-FR', {
                      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Note de passage</span>
                  <span className="font-medium">{selectedResult.passing_score} pts</span>
                </div>
              </div>

              {/* Feedback */}
              {selectedResult.my_submission.feedback && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm font-medium mb-2">Commentaire du formateur</p>
                  <p className="text-slate-600 dark:text-slate-400">
                    {selectedResult.my_submission.feedback}
                  </p>
                </div>
              )}

              {/* Download submitted file */}
              {selectedResult.my_submission.submission_file_url && (
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const fullUrl = resolveUploadUrl(selectedResult.my_submission.submission_file_url);
                    if (fullUrl) window.open(fullUrl, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger ma soumission
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvaluationsPage;
